import { z } from 'zod';

import { PlaceIdParamsSchema } from '../customer/place-customer.schemas.js';

export const FISCAL_DFE_DISTRIBUTION_CHECK_STATUSES = [
  'PENDING',
  'SUCCEEDED',
  'FAILED',
  'SKIPPED_COOLDOWN',
  'BLOCKED_656',
] as const;

export const RequestDfeDistributionCheckParamsSchema = PlaceIdParamsSchema;

export const RequestDfeDistributionCheckResponseSchema = z.looseObject({
  distributionCheckId: z.uuid(),
  status: z.literal('PENDING'),
  requestedAt: z.iso.datetime(),
});
export type RequestDfeDistributionCheckResponse = z.infer<
  typeof RequestDfeDistributionCheckResponseSchema
>;

export const ListDfeInboxDocumentsParamsSchema = PlaceIdParamsSchema;

export const ListDfeInboxDocumentsQuerySchema = z.looseObject({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListDfeInboxDocumentsQueryParams = z.infer<typeof ListDfeInboxDocumentsQuerySchema>;

export const DfeInboxDocumentItemSchema = z.looseObject({
  documentId: z.uuid(),
  accessKey: z.string().length(44),
  nsu: z.string(),
  issuerCnpj: z.string(),
  issuerName: z.string(),
  issuedAt: z.iso.datetime(),
  hasSummary: z.boolean(),
  hasFullXml: z.boolean(),
  manifestationStatus: z.string().nullable(),
  manifestationInFlight: z.boolean(),
  lastManifestationErrorCode: z.string().nullable(),
  lastManifestationErrorMessage: z.string().nullable(),
  materializedFiscalDocumentId: z.uuid().nullable(),
});
export type DfeInboxDocumentItem = z.infer<typeof DfeInboxDocumentItemSchema>;

export const ListDfeInboxDocumentsResponseSchema = z.looseObject({
  items: z.array(DfeInboxDocumentItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});
export type ListDfeInboxDocumentsResponse = z.infer<typeof ListDfeInboxDocumentsResponseSchema>;

/** Cursor page of distribution check events (inbox hierarchy parent rows). */
export const ListDfeDistributionChecksQuerySchema = z.looseObject({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListDfeDistributionChecksQueryParams = z.infer<
  typeof ListDfeDistributionChecksQuerySchema
>;

export const DfeDistributionCheckItemSchema = z.looseObject({
  distributionCheckId: z.uuid(),
  status: z.enum(FISCAL_DFE_DISTRIBUTION_CHECK_STATUSES),
  requestedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  nextAllowedAt: z.iso.datetime().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorMessage: z.string().nullable(),
  documentsCount: z.number().int().nonnegative(),
});
export type DfeDistributionCheckItem = z.infer<typeof DfeDistributionCheckItemSchema>;

export const ListDfeDistributionChecksResponseSchema = z.looseObject({
  items: z.array(DfeDistributionCheckItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});
export type ListDfeDistributionChecksResponse = z.infer<
  typeof ListDfeDistributionChecksResponseSchema
>;

export const DfeDistributionCheckIdParamsSchema = z.looseObject({
  placeId: z.uuid(),
  checkId: z.uuid(),
});
export type DfeDistributionCheckIdParams = z.infer<typeof DfeDistributionCheckIdParamsSchema>;

export const ListDfeCheckDocumentsQuerySchema = z.looseObject({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListDfeCheckDocumentsQueryParams = z.infer<typeof ListDfeCheckDocumentsQuerySchema>;

export const ListDfeCheckDocumentsResponseSchema = ListDfeInboxDocumentsResponseSchema;
export type ListDfeCheckDocumentsResponse = ListDfeInboxDocumentsResponse;

export const DfeInboxDocumentIdParamsSchema = z.looseObject({
  placeId: z.uuid(),
  documentId: z.uuid(),
});
export type DfeInboxDocumentIdParams = z.infer<typeof DfeInboxDocumentIdParamsSchema>;

export const GetDfeInboxDocumentResponseSchema = DfeInboxDocumentItemSchema;
export type GetDfeInboxDocumentResponse = z.infer<typeof GetDfeInboxDocumentResponseSchema>;

export const DFE_MANIFESTATION_TYPES = [
  'CIENCIA_DA_OPERACAO',
  'CONFIRMACAO_DA_OPERACAO',
  'DESCONHECIMENTO_DA_OPERACAO',
  'OPERACAO_NAO_REALIZADA',
] as const;
export type DfeManifestationType = (typeof DFE_MANIFESTATION_TYPES)[number];

export const RequestDfeManifestationBodySchema = z.looseObject({
  manifestationType: z.enum(DFE_MANIFESTATION_TYPES),
  justification: z.string().trim().min(15).max(255).optional(),
});
export type RequestDfeManifestationBody = z.infer<typeof RequestDfeManifestationBodySchema>;

export const RequestDfeManifestationResponseSchema = z.looseObject({
  documentId: z.uuid(),
  manifestationType: z.enum(DFE_MANIFESTATION_TYPES),
  manifestationInFlight: z.literal(true),
});
export type RequestDfeManifestationResponse = z.infer<typeof RequestDfeManifestationResponseSchema>;

export const GetDfeInboxDocumentXmlResponseSchema = z.looseObject({
  downloadUrl: z.url(),
  fileName: z.string(),
});
export type GetDfeInboxDocumentXmlResponse = z.infer<typeof GetDfeInboxDocumentXmlResponseSchema>;
